import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Tag, Button, Skeleton, Empty, message, Popconfirm, Statistic, Space, Typography, Alert, Progress } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, ClockCircleFilled, QuestionCircleFilled, EnvironmentOutlined, CalendarOutlined, ArrowLeftOutlined, MailOutlined, DollarOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import StadiumField from '../../components/community/StadiumField';
import useNewConfirmationSound from '../../hooks/useNewConfirmationSound';

const { Title, Text } = Typography;
const BRAND = '#25D366';

const STATUS_META = {
  going:     { label: 'Vão jogar',     color: '#52c41a', bg: '#f6ffed', icon: <CheckCircleFilled /> },
  not_going: { label: 'Não vão',       color: '#ff4d4f', bg: '#fff1f0', icon: <CloseCircleFilled /> },
  waiting:   { label: 'Na espera',     color: '#faad14', bg: '#fffbe6', icon: <ClockCircleFilled /> },
  pending:   { label: 'Sem resposta',  color: '#8c8c8c', bg: '#fafafa', icon: <QuestionCircleFilled /> },
};

// Painel real-time do organizador. Polling 5s. Owner-only (backend valida).
export default function ConvocationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/community/convocations/${id}`);
      setData(r.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) setError('nao_encontrada');
      else if (err.response?.status === 403) setError('sem_permissao');
      else setError('erro');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 10000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const cancel = async () => {
    try {
      await api.post(`/community/convocations/${id}/cancel`);
      message.success('Convocação cancelada');
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Erro');
    }
  };

  const sendToNew = async () => {
    try {
      const r = await api.post(`/community/convocations/${id}/send-to-new`);
      message.success(`Enviado pra ${r.data.added} novo(s) jogador(es) · ${r.data.credits_spent} crédito(s)`);
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Erro ao enviar');
    }
  };

  // Grouped precisa ser calculado ANTES do hook de som (regra dos hooks
  // — chamada precisa ser incondicional). useMemo evita reprocessar.
  const grouped = useMemo(() => {
    const g = { going: [], not_going: [], waiting: [], pending: [] };
    for (const r of (data?.responses || [])) g[r.status]?.push(r);
    return g;
  }, [data]);

  // Toca chime ao detectar novo confirmado (going). Toggle persiste em LS.
  const sound = useNewConfirmationSound(grouped.going, `conv-${id}`);

  if (loading && !data) return <div style={{ padding: 40 }}><Skeleton active /></div>;
  if (error === 'nao_encontrada') return <Empty description="Convocação não encontrada" style={{ padding: 60 }} />;
  if (error === 'sem_permissao') return <Empty description="Só o dono do time acessa" style={{ padding: 60 }} />;
  if (error) return <Empty description="Erro ao carregar" style={{ padding: 60 }} />;
  if (!data) return null;

  const c = data.convocation;
  const stats = data.stats || {};
  const isCancelled = c.status === 'cancelled';

  const respondedPct = c.total_recipients > 0
    ? Math.round(((c.total_recipients - (stats.pending || 0)) / c.total_recipients) * 100)
    : 0;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 16 }}>
      <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/time/jogos')} style={{ marginBottom: 8 }}>
        Voltar
      </Button>

      <Card style={{ borderRadius: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Space size={8} style={{ marginBottom: 4 }}>
              <Tag color={isCancelled ? 'red' : 'green'} icon={<MailOutlined />}>
                {isCancelled ? 'CANCELADA' : 'ABERTA'} · {c.channel.toUpperCase()}
              </Tag>
              <Tag color="blue">
                <DollarOutlined /> {c.credits_spent} crédito{c.credits_spent > 1 ? 's' : ''}
              </Tag>
            </Space>
            <Title level={3} style={{ margin: '4px 0' }}>{c.title}</Title>
            <div style={{ color: '#595959', fontSize: 14 }}>
              <CalendarOutlined /> {dayjs(c.match_date).format('dddd, DD/MM/YYYY')} · <strong>{String(c.match_time).slice(0, 5)}</strong>
              {c.quadra && <><br /><EnvironmentOutlined /> {c.quadra}{c.city ? ` · ${c.city}` : ''}</>}
            </div>
            {c.notes && (
              <div style={{ marginTop: 8, padding: 8, background: '#fafafa', borderRadius: 8, fontStyle: 'italic', color: '#555' }}>
                {c.notes}
              </div>
            )}
          </div>
          {!isCancelled && (
            <Popconfirm
              title="Cancelar convocação?"
              description="Emails já foram enviados — não há estorno de créditos."
              onConfirm={cancel}
              okText="Sim, cancelar"
              cancelText="Não"
            >
              <Button danger>Cancelar convocação</Button>
            </Popconfirm>
          )}
        </div>

        {isCancelled && (
          <Alert type="warning" showIcon icon={<WarningOutlined />}
            message="Esta convocação foi cancelada. Jogadores que abrirem o link verão o aviso."
            style={{ marginTop: 12 }} />
        )}

        {!isCancelled && data.new_recipients_count > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={
              <span>
                <strong>{data.new_recipients_count} jogador{data.new_recipients_count > 1 ? 'es' : ''}</strong>{' '}
                do elenco ainda não {data.new_recipients_count > 1 ? 'receberam' : 'recebeu'} essa convocação.
              </span>
            }
            action={
              <Popconfirm
                title={`Enviar pra ${data.new_recipients_count} jogador(es)?`}
                description={`Custo: ${data.new_recipients_cost} crédito${data.new_recipients_cost > 1 ? 's' : ''}. Vai disparar email pra quem ainda não recebeu.`}
                okText="Enviar"
                cancelText="Não"
                onConfirm={sendToNew}
              >
                <Button type="primary" size="small">
                  Enviar ({data.new_recipients_cost} cr)
                </Button>
              </Popconfirm>
            }
          />
        )}

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <Text strong>{c.total_recipients - (stats.pending || 0)} de {c.total_recipients} responderam</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{respondedPct}%</Text>
          </div>
          <Progress percent={respondedPct} strokeColor={BRAND} showInfo={false} />
        </div>
      </Card>

      {!isCancelled && (
        <Card size="small" style={{ borderRadius: 16, marginBottom: 12, padding: 12, background: '#0f172a' }}
              bodyStyle={{ padding: 0 }}>
          <div style={{ maxWidth: 380, margin: '0 auto' }}>
            <StadiumField
              going={grouped.going.map(r => ({ id: r.id, name: r.player_name }))}
              waiting={grouped.waiting.map(r => ({ id: r.id, name: r.player_name }))}
              notGoing={grouped.not_going.map(r => ({ id: r.id, name: r.player_name }))}
              emptyLabel="Ninguém respondeu ainda. Vai chegar 👊" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          marginTop: 10, fontSize: 11, color: '#94a3b8', padding: '0 4px' }}>
              <span>⚽ {stats.going || 0} · 🪑 {stats.waiting || 0} · ✋ {stats.not_going || 0}</span>
              <button
                onClick={sound.toggle}
                style={{
                  background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                  color: sound.enabled ? '#facc15' : '#94a3b8',
                  padding: '3px 10px', borderRadius: 999, fontSize: 11, cursor: 'pointer',
                  fontWeight: 600,
                }}
                title={sound.enabled ? 'Som ligado — desligar' : 'Som desligado — ativar toque'}>
                {sound.enabled ? '🔔 Som' : '🔕 Silêncio'}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Row gutter={[12, 12]}>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Vão" value={stats.going || 0} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleFilled />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Não vão" value={stats.not_going || 0} valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleFilled />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Na espera" value={stats.waiting || 0} valueStyle={{ color: '#faad14' }} prefix={<ClockCircleFilled />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Pendentes" value={stats.pending || 0} valueStyle={{ color: '#8c8c8c' }} prefix={<QuestionCircleFilled />} /></Card></Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        {['going', 'waiting', 'pending', 'not_going'].map(key => {
          const meta = STATUS_META[key];
          const list = grouped[key] || [];
          return (
            <Col xs={24} md={12} key={key}>
              <Card size="small" style={{ borderRadius: 12, background: meta.bg, minHeight: 200 }}
                title={<span style={{ color: meta.color, fontWeight: 700 }}>{meta.icon} {meta.label} ({list.length})</span>}>
                {list.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>Ninguém aqui ainda.</Text>
                ) : (
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    {list.map(r => (
                      <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 500 }}>{r.player_name}</div>
                          {r.email_ok === false && (
                            <Text type="danger" style={{ fontSize: 10 }}>⚠ email falhou: {r.email_error || 'erro'}</Text>
                          )}
                        </div>
                        {r.responded_at && (
                          <Text type="secondary" style={{ fontSize: 10 }}>
                            {dayjs(r.responded_at).format('DD/MM HH:mm')}
                          </Text>
                        )}
                      </div>
                    ))}
                  </Space>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Espaço reservado pra ads futuros */}
      <div style={{ marginTop: 16, minHeight: 60, borderRadius: 8, background: '#fafafa', border: '1px dashed #e0e0e0',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 11 }}>
        {/* ad slot reservado */}
      </div>
    </div>
  );
}
